// React Imports
import React, { useState } from "react";

// 3rd Party Imports
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import C from "classnames";

// Local Components
import { ScormExportDialog } from "../../ScormExportDialog/ScormExportDialog";
import S from "./LessonCard.module.scss";

// Hooks & Helpers
import { buildRoute } from "../../../../constants/routes";
import { useSmartNavigation } from "../../../../helpers/navigation";
import { isEmpty } from "../../../../utils";
import { getContentTagForDisplay, getVariantStyle } from "../../../utils";
import { buildLessonButtonText, getLessonBadgeSvg } from "../contentCardHelpers";
import { useUserContext } from "../../../contexts";

// Types
import { ContentCardProps } from "../contentCardTypes";

export const LessonCard: React.FC<ContentCardProps> = ({
  contentType,
  variant,
  points = 100,
  title,
  description,
  difficulty,
  estimatedTime,
  progress,
  status = "notStarted",
  lessonKey,
  inPath = false,
  disabled = false,
  required = true,
  catalogView = false,
  headingLevel: HeadingTag = "h4",
}) => {
  const { navigateTo } = useSmartNavigation();
  const { isTenantAdmin } = useUserContext();
  const [isScormDialogVisible, setIsScormDialogVisible] = useState(false);

  const navigateToContent = (): void => {
    if (contentType === "exercise") {
      navigateTo(
        buildRoute(
          "Learn.routes.exercise.route",
          { "*": lessonKey || "" },
          { ...(inPath && { inPath: "true" }) }
        )
      );
    } else {
      navigateTo(
        buildRoute(
          "Learn.routes.lesson.route",
          { lessonKey: lessonKey || "" },
          { ...(inPath && { inPath: "true" }) }
        )
      );
    }
  };

  const variantStyle = getVariantStyle(variant as string);

  const buttonText = buildLessonButtonText(contentType, status);
  const badgeSvg = getLessonBadgeSvg(contentType, status);

  const statusClass =
    status === "completed" ? S.lessonCompleted : status === "inProgress" ? S.lessonInProgress : "";

  const shouldDisplayScormExport = isTenantAdmin && catalogView;
  const safeId = lessonKey?.replace(/[^a-zA-Z0-9-_]/g, "-") || "unknown";

  return (
    <div className={C(S.lessonCard, statusClass, variantStyle)}>
      <Tooltip target={`#optional-icon-${safeId}`} content="Optional lesson" position="top" />
      <Tooltip target={`#export-scorm-${safeId}`} content="SCORM" position="top" />
      <div className={S.lessonCardHeader}>
        <Tag value={getContentTagForDisplay(contentType)} rounded={true} />
        {points && points > 0 && <span className={C(S.mutedText, "atlas-h5")}> +{points}</span>}
        <div className="ml-auto flex items-center gap-1">
          {!required && <i id={`optional-icon-${safeId}`} className="pi pi-lightbulb" />}
          {shouldDisplayScormExport && (
            <Button
              id={`export-scorm-${safeId}`}
              icon="pi pi-download"
              text
              rounded
              severity="contrast"
              aria-label="Export SCORM"
              className="p-0"
              onClick={() => setIsScormDialogVisible(true)}
            />
          )}
        </div>
      </div>
      <HeadingTag className={C("atlas-h4", "leading-none")}>{title}</HeadingTag>
      <p className={C("body-md", S.description, S.mutedText)}>{description}</p>
      <div className={S.lessonDetailsWrapper}>
        {difficulty && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Difficulty</p>
            <p className="body-md">{difficulty}</p>
          </div>
        )}
        {estimatedTime && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Estimated Time</p>
            <p className="body-md">~ {estimatedTime} mins</p>
          </div>
        )}
        {progress && !isEmpty(progress) && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Progress</p>
            <p className="body-md">
              {progress.completed}/{progress.totalLessons}
              Tasks
            </p>
          </div>
        )}
      </div>
      <div className={S.lessonCardFooter}>
        <Button label={buttonText} onClick={navigateToContent} disabled={disabled} />
      </div>
      {badgeSvg && (
        <div className={S.badgeSvgWrapper}>
          <img src={badgeSvg} alt={`${contentType} badge`} />
        </div>
      )}
      {shouldDisplayScormExport && isScormDialogVisible && (
        <ScormExportDialog
          visible
          onHide={() => setIsScormDialogVisible(false)}
          lessons={[
            { lessonKey: lessonKey || "", name: title || "", description: description || "" },
          ]}
        />
      )}
    </div>
  );
};
